import { CANVAS_WIDTH } from "../../game/constants";
import { clamp01, easeOutBack, easeOutCubic } from "../../shared/easing";
import { isMobileViewport } from "../../game/platform";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import type { LeaderboardEntry } from "../../game/types";
import type { SoloMode } from "../../game/types";
import { soloModeLabel } from "../../game/soloModes";
import {
  mFont,
  mFontPlain,
  menuButtonColumns,
  menuBtnH,
  roundedRectPath,
  resolveGameOverLayout,
  resolveSoloLeaderboardSurfaceState,
} from "../renderConstants";
import type { LeaderboardScrollConfig } from "../leaderboardScrollController";

// ── Types ────────────────────────────────────────────────────────────────

export interface GameOverFlashState {
  highScoreFlashTriggered: boolean;
  highScoreFlashMs: number;
  levelUpFlashTriggered: boolean;
  levelUpFlashMs: number;
}

export interface GameOverDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  soloSelectorLogoImages: Record<SoloMode, HTMLImageElement | null>;
  /** Mutable flash state — passed by reference (object). */
  flashState: GameOverFlashState;
  /** Helper callbacks forwarded from the renderer class. */
  drawCoinIcon: (x: number, y: number, size: number) => void;
  drawHourglass: (cx: number, cy: number, size: number, color: string) => void;
  resolveLeaderboardScroll: (config: LeaderboardScrollConfig) => number;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawGameOverScreen(deps: GameOverDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const t = model.gameOverElapsedSec;
  const centerX = CANVAS_WIDTH / 2;
  // --- Dark overlay ---
  ctx.fillStyle = "rgba(10, 5, 15, 0.55)";
  ctx.fillRect(0, 0, CANVAS_WIDTH, deps.canvasHeight);

  // --- New high score flash overlay ---
  if (model.isNewHighScore && !deps.flashState.highScoreFlashTriggered) {
    deps.flashState.highScoreFlashTriggered = true;
    deps.flashState.highScoreFlashMs = deps.nowMs();
  }
  if (deps.flashState.highScoreFlashTriggered) {
    const flashElapsed = (deps.nowMs() - deps.flashState.highScoreFlashMs) / 1000;
    if (flashElapsed < 0.5) {
      const flashAlpha = 0.3 * (1 - flashElapsed / 0.5);
      ctx.fillStyle = `rgba(255,215,0,${flashAlpha.toFixed(3)})`;
      ctx.fillRect(0, 0, CANVAS_WIDTH, deps.canvasHeight);
    }
  }
  if (model.phase !== "RUN_GAMEOVER") {
    deps.flashState.highScoreFlashTriggered = false;
  }

  if (model.phase !== "RUN_GAMEOVER") {
    deps.flashState.levelUpFlashTriggered = false;
  }

  // --- Shared: Game Mode Logo (all steps) ---
  const menuCols = menuButtonColumns(centerX);
  const rawStep = model.gameOverStep;
  const stepT = model.gameOverStepElapsed;
  const unlockCount = model.newlyUnlockedModes.length;
  // Step mapping: 0=level-up, 1..N=unlock celebration, N+1=coins, N+2=overview
  const unlockStepStart = 1;
  const coinsStep = unlockStepStart + unlockCount;
  const canvasH = deps.canvasHeight;
  const logoW = 200;
  const modeLogo = deps.soloSelectorLogoImages[model.latestRunSoloMode];
  const logoTargetY = 50;
  let logoY = logoTargetY;
  let logoH = 0;
  {
    const logoSlide = clamp01(t / 0.5);
    const logoAlpha = clamp01(t / 0.4);
    ctx.globalAlpha = logoAlpha;
    if (modeLogo != null) {
      const naturalW = modeLogo.naturalWidth || 200;
      const naturalH = modeLogo.naturalHeight || 40;
      logoH = Math.round(logoW * (naturalH / naturalW));
      const logoX = Math.round((CANVAS_WIDTH - logoW) / 2);
      logoY = logoTargetY - 20 * (1 - easeOutCubic(logoSlide));
      ctx.drawImage(modeLogo, logoX, logoY, logoW, logoH);
    }
    ctx.globalAlpha = 1;
  }

  // Lobby-style flat panel button helper
  const drawLobbyBtn = (rect: Rect, label: string, tint?: string): void => {
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.fillStyle = tint ?? "rgba(20,36,54,0.85)";
    ctx.fill();
    ctx.strokeStyle = "rgba(108,200,255,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#e9f7ff";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
  };

  // Content vertical center (below logo, above NEXT button)
  const contentTop = 110;
  const nextBtnY = canvasH - 70;
  const contentMidY = (contentTop + nextBtnY) / 2;

  // ===================== STEP 0 — Level Up =====================
  if (rawStep === 0) {
    const circleR = 40;
    const circleCy = contentMidY - 20;
    const ringWidth = 6;

    // Circle scales up from 0
    const scaleInT = clamp01(stepT / 0.5);
    const circleScale = easeOutBack(scaleInT);
    const fadeIn = clamp01(stepT / 0.4);
    ctx.globalAlpha = fadeIn;

    // XP fill animation (starts after circle appears)
    const fillAnimT = clamp01((stepT - 0.5) / 1.0);
    const fillEased = easeOutCubic(fillAnimT);
    let targetFill: number;
    if (model.levelUpTriggered) {
      if (stepT < 2.5) {
        targetFill = model.xpBeforeRun + (1 - model.xpBeforeRun) * fillEased;
      } else {
        const resetT = clamp01((stepT - 3.0) / 0.5);
        targetFill = model.xpProgress * easeOutCubic(resetT);
      }
    } else {
      targetFill = model.xpBeforeRun + (model.xpProgress - model.xpBeforeRun) * fillEased;
    }

    const displayLevel = model.levelUpTriggered && stepT >= 3.0
      ? model.newLevel
      : model.level;

    // Draw circle with scale transform
    ctx.save();
    ctx.translate(centerX, circleCy);
    ctx.scale(circleScale, circleScale);

    // Background ring
    ctx.beginPath();
    ctx.arc(0, 0, circleR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30,50,80,0.8)";
    ctx.lineWidth = ringWidth;
    ctx.stroke();

    // XP progress arc
    const xpFrac = clamp01(targetFill);
    if (xpFrac > 0) {
      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + Math.PI * 2 * xpFrac;
      ctx.beginPath();
      ctx.arc(0, 0, circleR, startAngle, endAngle);
      ctx.strokeStyle = "#ffd700";
      ctx.lineWidth = ringWidth;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.lineCap = "butt";

      // Glowing tip
      const tipX = Math.cos(endAngle) * circleR;
      const tipY = Math.sin(endAngle) * circleR;
      const tipPulse = 0.5 + 0.5 * Math.sin(deps.nowMs() / 300);
      ctx.save();
      ctx.shadowColor = "#ffd700";
      ctx.shadowBlur = 8 + 8 * tipPulse;
      ctx.beginPath();
      ctx.arc(tipX, tipY, ringWidth * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = "#fff8cc";
      ctx.fill();
      ctx.restore();
    }

    // Inner fill
    ctx.beginPath();
    ctx.arc(0, 0, circleR - ringWidth - 1, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(14,24,44,0.92)";
    ctx.fill();

    // Level number
    ctx.font = mFont("bold", 16);
    ctx.fillStyle = "#ffd700";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${displayLevel}`, 0, 1);

    ctx.restore(); // end circle scale

    // "+XP" amount text (slides up from below circle)
    const xpTextDelay = 0.7;
    if (stepT >= xpTextDelay) {
      const xpTextT = clamp01((stepT - xpTextDelay) / 0.4);
      const xpTextAlpha = easeOutCubic(xpTextT);
      const xpSlideY = 10 * (1 - easeOutCubic(xpTextT));
      ctx.globalAlpha = xpTextAlpha;

      // Compute XP gained as percentage difference displayed as points
      const xpGainedPct = model.levelUpTriggered
        ? Math.round((1 - model.xpBeforeRun + model.xpProgress) * 100)
        : Math.round((model.xpProgress - model.xpBeforeRun) * 100);
      const xpLabel = xpGainedPct > 0 ? `+${xpGainedPct} XP` : "0 XP";

      ctx.font = mFont("bold", 9);
      ctx.fillStyle = "#ffd700";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(xpLabel, centerX, circleCy + circleR + 16 + xpSlideY);
    }

    // "LEVEL UP!" text with scale animation
    if (model.levelUpTriggered && stepT >= 2.5) {
      const luLocalT = stepT - 2.5;
      const scaleT2 = clamp01(luLocalT / 0.5);
      const s = scaleT2 < 1 ? 0.5 + 1.5 * easeOutBack(scaleT2) : 1;
      ctx.globalAlpha = 1;
      ctx.save();
      ctx.translate(centerX, circleCy + circleR + 50);
      ctx.scale(s, s);
      ctx.font = mFont("bold", 11);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#ffd700";
      ctx.fillText("LEVEL UP!", 0, 0);
      ctx.restore();
    }

    ctx.globalAlpha = 1;

    // NEXT button (slides up from bottom)
    const nextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
    const nextDelay = model.levelUpTriggered ? 3.2 : 1.2;
    const nextAlpha = clamp01((stepT - nextDelay) / 0.3);
    const nextSlide = 20 * (1 - easeOutCubic(clamp01((stepT - nextDelay) / 0.4)));
    ctx.globalAlpha = nextAlpha;
    ctx.save();
    ctx.translate(0, nextSlide);
    drawLobbyBtn(nextRect, "NEXT");
    ctx.restore();
    ctx.globalAlpha = 1;

    return { gameOverNext: nextRect };
  }

  // ===================== UNLOCK STEPS — One per newly unlocked mode =====================
  if (rawStep >= unlockStepStart && rawStep < coinsStep) {
    const unlockIdx = rawStep - unlockStepStart;
    const unlockedMode = model.newlyUnlockedModes[unlockIdx];
    if (unlockedMode != null) {
      // Mode logo scales in from center
      const logoScaleT = clamp01(stepT / 0.5);
      const logoScale = easeOutBack(logoScaleT);
      const fadeIn = clamp01(stepT / 0.4);
      ctx.globalAlpha = fadeIn;

      const unlockLogo = deps.soloSelectorLogoImages[unlockedMode];
      const unlockLogoW = 240;
      const unlockLogoCy = contentMidY - 30;

      ctx.save();
      ctx.translate(centerX, unlockLogoCy);
      ctx.scale(logoScale, logoScale);

      if (unlockLogo != null) {
        const nw = unlockLogo.naturalWidth || 200;
        const nh = unlockLogo.naturalHeight || 40;
        const unlockLogoH = Math.round(unlockLogoW * (nh / nw));
        ctx.drawImage(unlockLogo, -unlockLogoW / 2, -unlockLogoH / 2, unlockLogoW, unlockLogoH);
      } else {
        // Fallback: draw mode name as text
        ctx.font = mFont("bold", 14);
        ctx.fillStyle = "#ffd700";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(soloModeLabel(unlockedMode), 0, 0);
      }

      ctx.restore();

      // "NEW MODE UNLOCKED!" text with golden glow, appears after logo
      if (stepT >= 0.4) {
        const textT = clamp01((stepT - 0.4) / 0.4);
        const textScale = easeOutBack(textT);
        const glowPulse = 0.5 + 0.5 * Math.sin(deps.nowMs() / 300);
        ctx.globalAlpha = clamp01((stepT - 0.4) / 0.3);
        ctx.save();
        ctx.translate(centerX, unlockLogoCy + 55);
        ctx.scale(textScale, textScale);
        ctx.shadowColor = "rgba(255,215,0,0.8)";
        ctx.shadowBlur = 8 + 6 * glowPulse;
        ctx.font = mFont("bold", 9);
        ctx.fillStyle = "#ffd700";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("NEW MODE UNLOCKED!", 0, 0);
        ctx.shadowBlur = 0;
        ctx.restore();
      }

      ctx.globalAlpha = 1;

      // NEXT button
      const nextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
      const nextAlpha = clamp01((stepT - 0.8) / 0.3);
      const nextSlide = 20 * (1 - easeOutCubic(clamp01((stepT - 0.8) / 0.4)));
      ctx.globalAlpha = nextAlpha;
      ctx.save();
      ctx.translate(0, nextSlide);
      drawLobbyBtn(nextRect, "NEXT");
      ctx.restore();
      ctx.globalAlpha = 1;

      return { gameOverNext: nextRect };
    }
  }

  // ===================== Coins Earned =====================
  if (rawStep === coinsStep) {
    const coinSz = 56;
    const coinCy = contentMidY - 30;
    const coinScaleT = clamp01(stepT / 0.5);
    const coinScale = easeOutBack(coinScaleT);
    const fadeIn = clamp01(stepT / 0.4);
    ctx.globalAlpha = fadeIn;

    ctx.save();
    ctx.translate(centerX, coinCy + coinSz / 2);
    ctx.scale(coinScale, coinScale);
    deps.drawCoinIcon(-coinSz / 2, -coinSz / 2, coinSz);
    ctx.restore();

    if (stepT >= 0.4) {
      const textSlide = 15 * (1 - easeOutCubic(clamp01((stepT - 0.4) / 0.4)));
      ctx.globalAlpha = clamp01((stepT - 0.4) / 0.3);

      const coinAnimT = clamp01((stepT - 0.4) / 0.6);
      const coinEased = easeOutCubic(coinAnimT);
      const displayCoins = Math.floor(model.coinsEarnedThisRun * coinEased);
      const coinLabel = displayCoins > 0 ? `+${displayCoins}` : "0";

      ctx.font = mFont("bold", 7);
      ctx.fillStyle = "#7ca8ea";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText("GOLD EARNED", centerX, coinCy + coinSz + 12 + textSlide);

      ctx.font = mFont("bold", 18);
      ctx.fillStyle = "#ffd700";
      ctx.textBaseline = "top";
      ctx.fillText(coinLabel, centerX, coinCy + coinSz + 30 + textSlide);
    }

    // Coin particles exploding from coin icon and falling
    if (stepT >= 0.6 && model.coinsEarnedThisRun > 0) {
      const pCount = 12;
      const pDur = 2.5;
      const elapsed = stepT - 0.6;
      if (elapsed < pDur) {
        for (let i = 0; i < pCount; i++) {
          const spawnDelay = (i / pCount) * 0.4;
          const pT = elapsed - spawnDelay;
          if (pT < 0) continue;
          const pLife = pT / (pDur - spawnDelay);
          if (pLife > 1) continue;
          const angle = -Math.PI / 2 + (i - pCount / 2) * 0.25;
          const speed = 80 + (i % 3) * 30;
          const gravity = 220;
          const px = centerX + Math.cos(angle) * speed * pT;
          const py = (coinCy + coinSz / 2) + Math.sin(angle) * speed * pT + 0.5 * gravity * pT * pT;
          const pSize = 16 + (i % 3) * 6;
          const pScale = 1 - pLife * 0.3;
          ctx.globalAlpha = 1;
          ctx.save();
          ctx.translate(px, py);
          ctx.scale(pScale, pScale);
          deps.drawCoinIcon(-pSize / 2, -pSize / 2, pSize);
          ctx.restore();
        }
      }
    }

    ctx.globalAlpha = 1;

    // NEXT button (slides up from bottom)
    const nextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
    const nextAlpha = clamp01((stepT - 0.8) / 0.3);
    const nextSlide = 20 * (1 - easeOutCubic(clamp01((stepT - 0.8) / 0.4)));
    ctx.globalAlpha = nextAlpha;
    ctx.save();
    ctx.translate(0, nextSlide);
    drawLobbyBtn(nextRect, "NEXT");
    ctx.restore();
    ctx.globalAlpha = 1;

    return { gameOverNext: nextRect };
  }

  // ===================== Overview + Leaderboard + Buttons =====================
  // (only reached after the coins/reward step has completed)
  // Score slides down from top
  const actualScore = model.latestRunScore;
  const scoreProgress = stepT >= 0.2 ? clamp01((stepT - 0.2) / 0.6) : 0;
  const scoreEased = easeOutCubic(scoreProgress);
  const displayedScore = Math.floor(actualScore * scoreEased);
  const statsAlpha = clamp01((stepT - 0.1) / 0.3);
  const scoreSlide = 15 * (1 - easeOutCubic(clamp01(stepT / 0.4)));
  const overviewLayout = resolveGameOverLayout(centerX, {
    canvasHeight: canvasH,
    logoY,
    logoHeight: logoH,
    hasNewHighScore: model.isNewHighScore
  });
  ctx.globalAlpha = statsAlpha;

  ctx.font = mFont("bold", 7);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = "#7ca8ea";
  ctx.fillText("SCORE", centerX, overviewLayout.totalScoreY + scoreSlide);
  ctx.font = mFont("bold", 14);
  ctx.fillStyle = "rgba(10,18,34,0.95)";
  ctx.fillText(`${displayedScore}`, centerX + 2, overviewLayout.totalScoreValueY + scoreSlide + 2);
  ctx.fillStyle = "#e0e8f0";
  ctx.fillText(`${displayedScore}`, centerX, overviewLayout.totalScoreValueY + scoreSlide);
  ctx.globalAlpha = 1;

  // New high score (scales in)
  if (model.isNewHighScore && scoreProgress >= 1) {
    const glowPulse = 0.5 + 0.5 * Math.sin(deps.nowMs() / 300);
    const hsScaleT = clamp01((stepT - 1.0) / 0.4);
    const hsScale = easeOutBack(hsScaleT);
    ctx.save();
    ctx.translate(centerX, overviewLayout.newHighScoreY);
    ctx.scale(hsScale, hsScale);
    ctx.shadowColor = "rgba(255,215,0,0.8)";
    ctx.shadowBlur = 8 + 6 * glowPulse;
    ctx.fillStyle = "#ffd700";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("NEW HIGH SCORE!", 0, 0);
    ctx.shadowBlur = 0;
    ctx.restore();
  }

  // Coins + XP stacked beneath the final score
  if (stepT >= 0.3) {
    const sumAlpha = clamp01((stepT - 0.3) / 0.3);
    ctx.globalAlpha = sumAlpha;
    const sumSlide = 10 * (1 - easeOutCubic(clamp01((stepT - 0.3) / 0.4)));

    // Coins centered under the score
    const coinDisplaySz = 14;
    const coinStr = `+${model.coinsEarnedThisRun}`;
    ctx.font = mFont("bold", 7);
    const coinTextW = ctx.measureText(coinStr).width;
    const coinGroupW = coinDisplaySz + 4 + coinTextW;
    const coinGroupX = centerX - coinGroupW / 2;
    ctx.fillStyle = "#ffd700";
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    deps.drawCoinIcon(coinGroupX, overviewLayout.coinsY + sumSlide - 1, coinDisplaySz);
    ctx.fillText(coinStr, coinGroupX + coinDisplaySz + 4, overviewLayout.coinsY + sumSlide);

    // XP centered below the coin row
    const xpGainedPct = model.levelUpTriggered
      ? Math.round((1 - model.xpBeforeRun + model.xpProgress) * 100)
      : Math.round((model.xpProgress - model.xpBeforeRun) * 100);
    const xpStr = xpGainedPct > 0 ? `+${xpGainedPct} XP` : "0 XP";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#ffd700";
    ctx.fillText(xpStr, centerX, overviewLayout.xpY + sumSlide);

    ctx.globalAlpha = 1;
  }

  // Leaderboard + buttons sit below the stacked summary
  const lb = overviewLayout.leaderboard;
  const lbW = lb.w;
  const start: Rect = overviewLayout.start;
  const mainMenu: Rect = overviewLayout.mainMenu;
  const uiRects: UiRects = { start, mainMenu };

  // Leaderboard slides down from top
  if (stepT >= 0.5 && lb.h > 0) {
    const lbAlpha = clamp01((stepT - 0.5) / 0.3);
    ctx.globalAlpha = lbAlpha;
    const lbSlideProgress = clamp01((stepT - 0.5) / 0.6);
    const lbSlideOffset = 30 * (1 - easeOutCubic(lbSlideProgress));
    ctx.save();
    ctx.translate(0, -lbSlideOffset);

    const lbX = lb.x;
    const lbY = lb.y;
    const soloLeaderboard = resolveSoloLeaderboardSurfaceState(
      model.leaderboard.slice(0, 100),
      model.leaderboardLoading,
      model.leaderboardMode,
      model.leaderboardDisplayMode
    );
    const entries = soloLeaderboard.entries;
    const highlightRank = model.latestRunRank != null && model.latestRunRank >= 1 && model.latestRunRank <= 100
      ? model.latestRunRank
      : null;

    // Panel (matches lobby button styling)
    roundedRectPath(ctx, lbX, lbY, lbW, lb.h, 6);
    ctx.fillStyle = "rgba(20,36,54,0.85)";
    ctx.fill();
    roundedRectPath(ctx, lbX, lbY, lbW, lb.h, 6);
    ctx.strokeStyle = "rgba(108,200,255,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Header row
    const headerY = lbY + 8;
    ctx.fillStyle = "#7a9cc7";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("#", lbX + 10, headerY);
    ctx.fillText("NAME", lbX + 44, headerY);
    ctx.textAlign = "right";
    ctx.fillText("SCORE", lbX + lbW - 12, headerY);
    ctx.textAlign = "left";
    ctx.strokeStyle = "rgba(108,200,255,0.25)";
    ctx.beginPath();
    ctx.moveTo(lbX + 8, headerY + 9);
    ctx.lineTo(lbX + lbW - 8, headerY + 9);
    ctx.stroke();

    const drawRow = (
      rank: number,
      entry: LeaderboardEntry,
      yTop: number,
      highlighted: boolean
    ): void => {
      if (highlighted) {
        ctx.fillStyle = "rgba(0,255,206,0.12)";
        ctx.fillRect(lbX + 8, yTop, lbW - 16, 13);
        ctx.strokeStyle = "rgba(0,255,220,0.75)";
        ctx.strokeRect(lbX + 8.5, yTop + 0.5, lbW - 17, 12);
      } else if ((rank - 1) % 2 === 0) {
        ctx.fillStyle = "rgba(130,170,255,0.06)";
        ctx.fillRect(lbX + 8, yTop, lbW - 16, 13);
      }
      ctx.font = mFont("bold", 7);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillStyle = rank === 1 ? "#ffd700" : rank === 2 ? "#c0c0c0" : rank === 3 ? "#cd7f32" : "#b8d4ff";
      ctx.fillText(`${rank}.`, lbX + 10, yTop + 2);
      ctx.fillStyle = highlighted ? "#8bfff5" : rank <= 3 ? "#e7f0ff" : "#9fc4ff";
      ctx.fillText(entry.name, lbX + 44, yTop + 2);
      ctx.textAlign = "right";
      ctx.fillStyle = highlighted ? "#7effff" : rank === 1 ? "#ffd700" : "#d4e7ff";
      ctx.fillText(Math.floor(entry.score).toLocaleString(), lbX + lbW - 10, yTop + 2);
    };

    const rowH = isMobileViewport() ? 17 : 14;
    const viewportTop = headerY + 11;
    const viewportH = Math.max(0, lb.h - (viewportTop - lbY) - 8);
    const viewportRect: Rect = { x: lbX + 8, y: viewportTop, w: lbW - 16, h: viewportH };

    if (entries.length === 0 && soloLeaderboard.loading) {
      deps.drawHourglass(lbX + lbW / 2, viewportTop + viewportH / 2 - 10, 24, "#69e6ff");
      ctx.fillStyle = "#7a9cc7";
      ctx.font = mFont("bold", 6);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("LOADING SCORES", lbX + lbW / 2, viewportTop + viewportH / 2 + 18);
    } else if (entries.length === 0) {
      ctx.fillStyle = "#7a9cc7";
      ctx.font = mFontPlain(7);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("NO SCORES YET", lbX + lbW / 2, viewportTop + viewportH / 2);
    } else {
      const totalHeight = entries.length * rowH;
      const maxOffset = Math.max(0, totalHeight - viewportH);
      let scrollOffset = 0;

      if (highlightRank != null && highlightRank > 0) {
        const scrollDuration = Math.min(3.0, 0.8 + highlightRank * 0.04);
        const rankPixel = (highlightRank - 1) * rowH;
        const targetOffset = Math.min(maxOffset, Math.max(0, rankPixel - viewportH * 0.4));
        const scrollSpeed = targetOffset > 0 ? targetOffset / scrollDuration : 0;
        scrollOffset = deps.resolveLeaderboardScroll({
          kind: "target",
          maxOffset,
          targetOffset,
          speedPxPerSec: scrollSpeed
        });
      } else {
        scrollOffset = deps.resolveLeaderboardScroll({
          kind: "target",
          maxOffset,
          targetOffset: 0,
          speedPxPerSec: 0
        });
      }

      ctx.save();
      ctx.beginPath();
      ctx.rect(viewportRect.x, viewportRect.y, viewportRect.w, viewportRect.h);
      ctx.clip();
      for (let i = 0; i < entries.length; i++) {
        const yTop = viewportTop + i * rowH - scrollOffset;
        if (yTop + rowH < viewportTop || yTop > viewportTop + viewportH) continue;
        drawRow(i + 1, entries[i], yTop, highlightRank === i + 1);
      }
      ctx.restore();
    }

    uiRects.leaderboardScrollViewport = viewportRect;

    ctx.restore(); // leaderboard slide-up
    ctx.globalAlpha = 1;
  }

  // Buttons slide in from sides
  if (stepT >= 1.0) {
    const btnAlpha = clamp01((stepT - 1.0) / 0.3);
    ctx.globalAlpha = btnAlpha;
    const btnSlideProgress = clamp01((stepT - 1.0) / 0.4);
    const btnSlideEased = easeOutCubic(btnSlideProgress);
    const btnSlideX = 40 * (1 - btnSlideEased);

    ctx.save();
    ctx.translate(-btnSlideX, 0);
    drawLobbyBtn(start, "RETRY");
    ctx.restore();

    ctx.save();
    ctx.translate(btnSlideX, 0);
    drawLobbyBtn(mainMenu, "EXIT");
    ctx.restore();

    ctx.globalAlpha = 1;
  }

  // PWA install button (lobby style)
  if (model.showInstallApp && stepT >= 1.3) {
    const installAlpha = clamp01((stepT - 1.3) / 0.3);
    ctx.globalAlpha = installAlpha;
    const installRect: Rect = { x: menuCols.fullX, y: start.y + start.h + 6, w: menuCols.fullW, h: menuBtnH() };
    drawLobbyBtn(installRect, "INSTALL APP", "rgba(10,28,20,0.85)");
    uiRects.installApp = installRect;
    ctx.globalAlpha = 1;
  }

  // Score submission failure banner + retry
  if (model.scoreSubmitFailed && rawStep >= overviewStep) {
    const bannerY = start.y - menuBtnH() - 8;
    const bannerRect: Rect = { x: menuCols.fullX, y: bannerY, w: menuCols.fullW, h: menuBtnH() };
    const pulse = 0.7 + 0.3 * Math.sin(deps.nowMs() / 400);
    ctx.globalAlpha = pulse;
    drawLobbyBtn(bannerRect, "SCORE NOT SAVED — TAP TO RETRY", "rgba(80,20,20,0.9)");
    ctx.globalAlpha = 1;
    uiRects.scoreRetry = bannerRect;
  }

  return uiRects;
}
